import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  limit,
  onSnapshot,
  serverTimestamp,
  increment,
  Timestamp,
  writeBatch,
  runTransaction,
  setDoc
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { 
  User, 
  UserWallet, 
  WalletTransaction, 
  Donation, 
  ChapterPurchase,
  UserLocation,
  PurchaseTier,
  LocationKey
} from '../types/firebase';

// Collections
const USERS_COLLECTION = 'users';
const WALLETS_COLLECTION = 'wallets';
const TRANSACTIONS_COLLECTION = 'transactions';
const DONATIONS_COLLECTION = 'donations';
const CHAPTER_PURCHASES_COLLECTION = 'chapter_purchases';
const PAYMENT_VERIFICATIONS_COLLECTION = 'payment_verifications';

// Purchase tiers configuration
export const PURCHASE_TIERS: Record<LocationKey, PurchaseTier[]> = {
  india: [
    { id: 1, baseVos: 50, bonusVos: 2, totalVos: 52, price: 50, currency: 'INR' },
    { id: 2, baseVos: 100, bonusVos: 10, totalVos: 110, price: 100, currency: 'INR' },
    { id: 3, baseVos: 250, bonusVos: 35, totalVos: 285, price: 250, currency: 'INR' },
    { id: 4, baseVos: 500, bonusVos: 85, totalVos: 585, price: 500, currency: 'INR' },
    { id: 5, baseVos: 1000, bonusVos: 200, totalVos: 1200, price: 1000, currency: 'INR' },
  ],
  international: [
    { id: 1, baseVos: 5, bonusVos: 0, totalVos: 5, price: 5, currency: 'USD' },
    { id: 2, baseVos: 10, bonusVos: 1, totalVos: 11, price: 10, currency: 'USD' },
    { id: 3, baseVos: 25, bonusVos: 3, totalVos: 28, price: 25, currency: 'USD' },
    { id: 4, baseVos: 50, bonusVos: 8, totalVos: 58, price: 50, currency: 'USD' },
    { id: 5, baseVos: 100, bonusVos: 20, totalVos: 120, price: 100, currency: 'USD' },
  ]
};

// Chapter pricing
export const CHAPTER_PRICING: Record<LocationKey, number> = {
  india: 19.0,
  international: 0.99
};

// User services
export const userService = {
  async createUser(userData: Partial<User>): Promise<void> {
    const userRef = doc(db, USERS_COLLECTION, userData.uid!);
    await setDoc(userRef, {
      ...userData,
      createdAt: serverTimestamp(),
      lastLoginAt: serverTimestamp()
    }, { merge: true });
  },

  async getUser(uid: string): Promise<User | null> {
    const userRef = doc(db, USERS_COLLECTION, uid);
    const userSnap = await getDoc(userRef);

    console.log('userSnap====>:', userSnap);
    
    if (userSnap.exists()) {
      const data = userSnap.data();
      return {
        uid: userSnap.id,
        email: data.email,
        displayName: data.displayName,
        photoURL: data.photoURL,
        createdAt: data.createdAt?.toDate() || new Date(),
        lastLoginAt: data.lastLoginAt?.toDate() || new Date()
      };
    }
    
    return null;
  },

  async updateUser(uid: string, userData: Partial<User>): Promise<void> {
    const userRef = doc(db, USERS_COLLECTION, uid);
    await updateDoc(userRef, {
      ...userData,
      lastLoginAt: serverTimestamp()
    });
  }
};

// Wallet services - SERVER-SIDE VERIFICATION
export const walletService = {
  async getOrCreateWallet(userId: string): Promise<UserWallet> {
    const walletRef = doc(db, WALLETS_COLLECTION, userId);
    const walletSnap = await getDoc(walletRef);
    
    if (walletSnap.exists()) {
      const data = walletSnap.data();
      return {
        userId: walletSnap.id,
        balance: data.balance || 0,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date()
      };
    } else {
      // Create new wallet
      const newWallet = {
        userId,
        balance: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      await setDoc(walletRef, newWallet);
      
      return {
        userId,
        balance: 0,
        createdAt: new Date(),
        updatedAt: new Date()
      };
    }
  },

  // SECURE: Update wallet balance with transaction verification
  async updateWalletBalance(userId: string, amount: number): Promise<void> {
    const walletRef = doc(db, WALLETS_COLLECTION, userId);
    
    // Use transaction to ensure atomic update
    await runTransaction(db, async (transaction) => {
      const walletDoc = await transaction.get(walletRef);
      const currentBalance = walletDoc.exists() ? walletDoc.data().balance || 0 : 0;
      
      // Prevent negative balance for spending operations
      if (amount < 0 && currentBalance + amount < 0) {
        throw new Error('Insufficient balance');
      }
      
      transaction.update(walletRef, {
        balance: increment(amount),
        updatedAt: serverTimestamp()
      });
    });
  },

  // SECURE: Always fetch balance from server
  async getWalletBalance(userId: string): Promise<number> {
    const wallet = await this.getOrCreateWallet(userId);
    return wallet.balance;
  },

  // SECURE: Verify sufficient balance before allowing operations
  async verifyBalanceForPurchase(userId: string, requiredAmount: number): Promise<boolean> {
    const currentBalance = await this.getWalletBalance(userId);
    return currentBalance >= requiredAmount;
  }
};

// Transaction services - SECURE PAYMENT VERIFICATION
export const transactionService = {
  async createTransaction(transaction: Omit<WalletTransaction, 'id' | 'createdAt'>): Promise<string> {
    const transactionData = {
      ...transaction,
      createdAt: serverTimestamp(),
      status: 'completed',
      verifiedAt: serverTimestamp() // Server-side verification timestamp
    };
    
    const docRef = await addDoc(collection(db, TRANSACTIONS_COLLECTION), transactionData);
    return docRef.id;
  },

  async getTransactions(userId: string, limitCount: number = 20): Promise<WalletTransaction[]> {
    const q = query(
      collection(db, TRANSACTIONS_COLLECTION),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date()
    } as WalletTransaction));
  },

  // SECURE: Verify payment before processing
  async verifyAndProcessPayment(
    userId: string, 
    paymentId: string, 
    amount: number, 
    tier: PurchaseTier
  ): Promise<boolean> {
    try {
      // Store payment verification record
      const verificationData = {
        userId,
        paymentId,
        amount,
        tier,
        status: 'verified',
        verifiedAt: serverTimestamp(),
        processed: false
      };
      
      const verificationRef = await addDoc(
        collection(db, PAYMENT_VERIFICATIONS_COLLECTION), 
        verificationData
      );

      // Use batch write for atomic operations
      const batch = writeBatch(db);
      
      // Update wallet balance
      const walletRef = doc(db, WALLETS_COLLECTION, userId);
      batch.update(walletRef, {
        balance: increment(tier.baseVos),
        updatedAt: serverTimestamp()
      });
      
      // Create base transaction
      const baseTransactionRef = doc(collection(db, TRANSACTIONS_COLLECTION));
      batch.set(baseTransactionRef, {
        userId,
        transactionType: 'purchase',
        amount: tier.baseVos,
        description: `Purchased ${tier.baseVos} vos (${tier.currency} ${tier.price})`,
        paymentMethod: tier.currency === 'INR' ? 'razorpay' : 'paypal',
        paymentId,
        status: 'completed',
        createdAt: serverTimestamp(),
        verificationId: verificationRef.id
      });
      
      // Add bonus VOS if applicable
      if (tier.bonusVos > 0) {
        batch.update(walletRef, {
          balance: increment(tier.bonusVos)
        });
        
        const bonusTransactionRef = doc(collection(db, TRANSACTIONS_COLLECTION));
        batch.set(bonusTransactionRef, {
          userId,
          transactionType: 'bonus',
          amount: tier.bonusVos,
          description: `Bonus ${tier.bonusVos} vos for ${tier.currency} ${tier.price} purchase`,
          paymentMethod: tier.currency === 'INR' ? 'razorpay' : 'paypal',
          paymentId,
          status: 'completed',
          createdAt: serverTimestamp(),
          verificationId: verificationRef.id
        });
      }
      
      // Mark verification as processed
      batch.update(verificationRef, { processed: true });
      
      await batch.commit();
      return true;
      
    } catch (error) {
      console.error('Payment verification failed:', error);
      return false;
    }
  }
};

// Donation services
export const donationService = {
  async createDonation(donation: Omit<Donation, 'id' | 'createdAt'>): Promise<string> {
    const donationData = {
      ...donation,
      createdAt: serverTimestamp(),
      status: 'completed',
      verifiedAt: serverTimestamp() // Server-side verification
    };
    
    const docRef = await addDoc(collection(db, DONATIONS_COLLECTION), donationData);
    return docRef.id;
  },

  async getTopDonors(chapterId: number, limitCount: number = 10): Promise<any[]> {
    const targetChapter = chapterId > 1 ? chapterId - 1 : 1;
    
    const q = query(
      collection(db, DONATIONS_COLLECTION),
      where('chapterId', '==', targetChapter),
      orderBy('amount', 'desc'),
      limit(limitCount)
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc, index) => ({
      userName: doc.data().userName,
      amount: doc.data().amount,
      donationDate: doc.data().createdAt?.toDate().toDateString() || new Date().toDateString(),
      rank: index + 1
    }));
  },

  async getUserDonations(userId: string): Promise<Donation[]> {
    const q = query(
      collection(db, DONATIONS_COLLECTION),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date()
    } as Donation));
  }
};

// Chapter purchase services - SECURE ACCESS CONTROL
export const chapterService = {
  // SECURE: Purchase chapter with balance verification
  async purchaseChapter(userId: string, chapterId: number, countryCode: string): Promise<{
    success: boolean;
    message: string;
    purchaseId?: string;
  }> {
    try {
      const locationKey = locationService.getLocationKey(countryCode);
      const chapterPrice = CHAPTER_PRICING[locationKey];
      
      // Verify sufficient balance
      const hasBalance = await walletService.verifyBalanceForPurchase(userId, chapterPrice);
      if (!hasBalance) {
        return {
          success: false,
          message: `Insufficient balance. Required: ${chapterPrice} VOS`
        };
      }
      
      // Check if already purchased
      const alreadyPurchased = await this.hasUserPurchasedChapter(userId, chapterId);
      if (alreadyPurchased) {
        return {
          success: false,
          message: 'Chapter already purchased'
        };
      }
      
      // Use transaction for atomic purchase
      const purchaseId = await runTransaction(db, async (transaction) => {
        // Deduct balance
        const walletRef = doc(db, WALLETS_COLLECTION, userId);
        const walletDoc = await transaction.get(walletRef);
        const currentBalance = walletDoc.data()?.balance || 0;
        
        if (currentBalance < chapterPrice) {
          throw new Error('Insufficient balance');
        }
        
        transaction.update(walletRef, {
          balance: increment(-chapterPrice),
          updatedAt: serverTimestamp()
        });
        
        // Create purchase record
        const purchaseRef = doc(collection(db, CHAPTER_PURCHASES_COLLECTION));
        const purchaseData = {
          userId,
          chapterId,
          vosSpent: chapterPrice,
          purchasedAt: serverTimestamp(),
          status: 'completed',
          verifiedAt: serverTimestamp(),
          countryCode,
          priceAtPurchase: chapterPrice
        };
        
        transaction.set(purchaseRef, purchaseData);
        
        // Create transaction record
        const transactionRef = doc(collection(db, TRANSACTIONS_COLLECTION));
        transaction.set(transactionRef, {
          userId,
          transactionType: 'spend',
          amount: chapterPrice,
          description: `Purchased Chapter ${chapterId}`,
          createdAt: serverTimestamp(),
          status: 'completed',
          relatedPurchaseId: purchaseRef.id
        });
        
        return purchaseRef.id;
      });
      
      return {
        success: true,
        message: 'Chapter purchased successfully',
        purchaseId
      };
      
    } catch (error) {
      console.error('Chapter purchase failed:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Purchase failed'
      };
    }
  },

  // SECURE: Always verify from server
  async getPurchasedChapters(userId: string): Promise<number[]> {
    const q = query(
      collection(db, CHAPTER_PURCHASES_COLLECTION),
      where('userId', '==', userId),
      where('status', '==', 'completed'),
      orderBy('purchasedAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => doc.data().chapterId);
  },

  // SECURE: Server-side access verification
  async hasUserPurchasedChapter(userId: string, chapterId: number): Promise<boolean> {
    const q = query(
      collection(db, CHAPTER_PURCHASES_COLLECTION),
      where('userId', '==', userId),
      where('chapterId', '==', chapterId),
      where('status', '==', 'completed')
    );
    
    const querySnapshot = await getDocs(q);
    return !querySnapshot.empty;
  },

  // SECURE: Verify chapter access before allowing content
  async verifyChapterAccess(userId: string, chapterId: number): Promise<{
    hasAccess: boolean;
    reason: string;
  }> {
    // Free chapters (1-4) are always accessible
    if (chapterId <= 4) {
      return { hasAccess: true, reason: 'Free chapter' };
    }
    
    // Check if user has purchased the chapter
    const hasPurchased = await this.hasUserPurchasedChapter(userId, chapterId);
    if (hasPurchased) {
      return { hasAccess: true, reason: 'Chapter purchased' };
    }
    
    return { hasAccess: false, reason: 'Chapter not purchased' };
  }
};

// Location services
export const locationService = {
  async getUserLocation(): Promise<UserLocation> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
      
      console.log('Fetching location from ipwhois.io...');
      const response = await fetch('https://ipwhois.app/json/', {
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      
      if (!response.ok) throw new Error(`Location API failed with status: ${response.status}`);
      const data = await response.json();
      
      console.log('Location API response:', data);
      
      // Map ipwhois.io response to our UserLocation format
      return {
        country: data.country || 'Unknown',
        countryCode: data.country_code || 'XX',
        currency: data.currency || 'USD',
        ip: data.ip || 'unknown'
      };
    } catch (error) {
      console.error('Error getting user location:', error);
      console.log('Using fallback location: India');
      // Return India as default (matches existing behavior)
      return {
        country: 'India',
        countryCode: 'IN',
        currency: 'INR',
        ip: 'unknown'
      };
    }
  },

  getLocationKey(countryCode: string): LocationKey {
    return countryCode.toLowerCase() === 'in' ? 'india' : 'international';
  },

  getPurchaseTiers(countryCode: string): PurchaseTier[] {
    const locationKey = this.getLocationKey(countryCode);
    return PURCHASE_TIERS[locationKey];
  },

  getChapterPrice(countryCode: string): number {
    const locationKey = this.getLocationKey(countryCode);
    return CHAPTER_PRICING[locationKey];
  }
};